import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

interface UIState {
  theme: 'light' | 'dark' | 'system'
  language: string
  direction: 'ltr' | 'rtl'
  sidebarOpen: boolean
  sidebarCollapsed: boolean
  soundEnabled: boolean
  notificationsEnabled: boolean
}

const initialState: UIState = {
  theme: (() => {
    // Check localStorage or system preference (same logic as original DarkModeContext)
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) {
        return JSON.parse(saved) ? 'dark' : 'light';
      }
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'system';
  })(),
  language: (() => {
    // Initialize language from localStorage (i18next stores it as 'i18nextLng')
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('i18nextLng');
      return saved || 'en';
    }
    return 'en';
  })(),
  direction: (() => {
    // Initialize direction based on language
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('i18nextLng');
      return ['fa', 'ps', 'ar'].includes(saved || '') ? 'rtl' : 'ltr';
    }
    return 'ltr';
  })(),
  sidebarOpen: false,
  sidebarCollapsed: false,
  soundEnabled: true,
  notificationsEnabled: true,
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload
    },
    toggleTheme: (state) => {
      if (state.theme === 'light') {
        state.theme = 'dark'
      } else if (state.theme === 'dark') {
        state.theme = 'light'
      } else {
        // System theme - toggle based on current system preference
        state.theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'light' : 'dark'
      }
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload
      state.direction = ['fa', 'ps', 'ar'].includes(action.payload) ? 'rtl' : 'ltr'
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload
    },
    toggleSound: (state) => {
      state.soundEnabled = !state.soundEnabled
    },
    toggleNotifications: (state) => {
      state.notificationsEnabled = !state.notificationsEnabled
    },
  },
})

export const { setTheme, toggleTheme, setLanguage, toggleSidebar, setSidebarCollapsed, toggleSound, toggleNotifications } = uiSlice.actions
export default uiSlice.reducer
